import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listAvailableAggregators, createAggregatorGrant } from '../../services/api'

interface Props {
  projectId: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  /** When true, render nothing until there is at least one aggregator to offer —
   *  used for the post-creation prompt so inventors with no managing aggregator
   *  are never shown an empty dialog (T10). */
  hideIfEmpty?: boolean
  /** Optional intro line shown above the selector (e.g. the post-creation nudge). */
  intro?: string
}

export default function GrantAggregatorAccessDialog({ projectId, isOpen, onClose, onSuccess, hideIfEmpty = false, intro }: Props) {
  const queryClient = useQueryClient()

  const [selectedAggregatorId, setSelectedAggregatorId] = useState('')
  const [expiresEnabled, setExpiresEnabled] = useState(false)
  const [expiresInDays, setExpiresInDays] = useState('30')

  const { data: aggregators = [], isLoading } = useQuery({
    queryKey: ['available-aggregators', projectId],
    queryFn: () => listAvailableAggregators(projectId),
    enabled: isOpen,
  })

  function handleClose() {
    setSelectedAggregatorId('')
    setExpiresEnabled(false)
    setExpiresInDays('30')
    onClose()
  }

  const grantMutation = useMutation({
    mutationFn: () => {
      const days = Math.max(1, Math.min(3650, parseInt(expiresInDays || '30', 10) || 30))
      const expiresAt = expiresEnabled
        ? new Date(Date.now() + days * 86_400_000).toISOString()
        : undefined
      return createAggregatorGrant(projectId, selectedAggregatorId, expiresAt)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aggregator-grants', projectId] })
      setSelectedAggregatorId('')
      setExpiresEnabled(false)
      setExpiresInDays('30')
      onSuccess()
    },
  })

  if (!isOpen) return null
  // Post-creation prompt mode: stay invisible until we know there is at least one
  // managing aggregator to offer, so unmanaged inventors see nothing (T10).
  if (hideIfEmpty && (isLoading || aggregators.length === 0)) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="grant-dialog">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Grant Aggregator Access</h3>
        {intro && <p className="text-sm text-gray-600 mb-4" data-testid="grant-intro">{intro}</p>}

        {isLoading && (
          <p className="text-sm text-gray-500 mb-4" data-testid="aggregators-loading">
            Loading available aggregators…
          </p>
        )}

        {!isLoading && aggregators.length === 0 && (
          <p className="text-sm text-gray-500 mb-4" data-testid="no-aggregators">
            No aggregators available to grant access to.
          </p>
        )}

        {!isLoading && aggregators.length > 0 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="aggregator-select">
                Aggregator
              </label>
              <select
                id="aggregator-select"
                value={selectedAggregatorId}
                onChange={(e) => setSelectedAggregatorId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="aggregator-select"
              >
                <option value="">Select an aggregator…</option>
                {aggregators.map((a) => (
                  <option key={a.aggregator_id} value={a.aggregator_id}>
                    {a.aggregator_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="expires-enabled"
                type="checkbox"
                checked={expiresEnabled}
                onChange={(e) => {
                  setExpiresEnabled(e.target.checked)
                  if (!e.target.checked) setExpiresInDays('30')
                }}
                className="rounded border-gray-300"
                data-testid="expires-enabled-checkbox"
              />
              <label htmlFor="expires-enabled" className="text-sm text-gray-700">
                Set expiry date
              </label>
            </div>

            {expiresEnabled && (
              <div className="space-y-3 rounded-md bg-gray-50 p-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="expires-days">
                    Expires in (days)
                  </label>
                  <input
                    id="expires-days"
                    type="text"
                    inputMode="numeric"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="expires-days-input"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {grantMutation.isError && (
          <p className="mt-3 text-sm text-red-600" data-testid="grant-error">
            Failed to grant access. Please try again.
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            data-testid="grant-dialog-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => grantMutation.mutate()}
            disabled={!selectedAggregatorId || grantMutation.isPending}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="grant-dialog-submit"
          >
            {grantMutation.isPending ? 'Granting…' : 'Grant Access'}
          </button>
        </div>
      </div>
    </div>
  )
}
